type FindLastPopulatedPage = {
  requestedPage: number;
  isPagePopulated: (pageNumber: number, abortSignal?: AbortSignal) => Promise<boolean>;
  knownPopulatedPage?: number;
  abortSignal?: AbortSignal;
};

const throwIfAborted = (abortSignal?: AbortSignal) => {
  if (abortSignal?.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError');
  }
};

export const findLastPopulatedPage = async ({
  requestedPage,
  isPagePopulated,
  knownPopulatedPage = 1,
  abortSignal,
}: FindLastPopulatedPage): Promise<number> => {
  const normalizedRequestedPage = Number.isFinite(requestedPage) ? Math.max(1, Math.floor(requestedPage)) : 1;

  if (normalizedRequestedPage === 1) {
    return 1;
  }

  const highestPossiblePage = normalizedRequestedPage - 1;

  const normalizedKnownPopulatedPage = Math.min(
    highestPossiblePage,
    Math.max(1, Number.isFinite(knownPopulatedPage) ? Math.floor(knownPopulatedPage) : 1)
  );

  let lowerBoundary = normalizedKnownPopulatedPage + 1;
  let upperBoundary = highestPossiblePage;
  let lastPopulatedPage = normalizedKnownPopulatedPage;

  while (lowerBoundary <= upperBoundary) {
    throwIfAborted(abortSignal);

    const candidatePage = Math.floor((lowerBoundary + upperBoundary) / 2);
    const isPopulated = await isPagePopulated(candidatePage, abortSignal);

    throwIfAborted(abortSignal);

    if (isPopulated) {
      lastPopulatedPage = candidatePage;
      lowerBoundary = candidatePage + 1;
    } else {
      upperBoundary = candidatePage - 1;
    }
  }

  return lastPopulatedPage;
};
